/**
 * <s-seasonal-effects> — drifting seasonal particles plus an add-to-cart burst.
 *
 * Attributes (set by blocks/seasonal-effects.liquid):
 *   data-season  — auto | fall | winter | spring | summer | off (off = burst only)
 *   data-density — low | medium | high
 *   data-burst   — present to celebrate add to cart (s-cart:opened, fired after an add)
 *
 * Children: canvas[data-drift] (fixed, behind the sticky header), canvas[data-burst-layer]
 * (popover="manual" so it paints above the open cart drawer in the top layer), and
 * button[data-toggle] (pause/play, aria-pressed). Pause state persists in localStorage.
 * prefers-reduced-motion: reduce disables both drift and burst. Particles freeze while the
 * tab is hidden. Particle colours are canvas paint, not CSS, so they live here as hex.
 */
const PALETTES = {
  fall: { colors: ['#D9692B', '#B8462E', '#E9A23B', '#9A6232'], shape: 'leaf' },
  winter: { colors: ['#9DB7D5', '#BFD0E6', '#7FA3CC', '#D6E2F0'], shape: 'flake' },
  spring: { colors: ['#F4A7B9', '#F7C6D3', '#E68AA5', '#FFD6A5'], shape: 'petal' },
  summer: { colors: ['#8FD3F4', '#BDE6FA', '#6EC1EA', '#A8E6CF'], shape: 'cube' },
};
const COUNTS = { low: 14, medium: 26, high: 44 };
const STORAGE_KEY = 'tovolo-seasonal-paused';

function seasonForDate(date = new Date()) {
  const m = date.getMonth();
  if (m >= 8 && m <= 10) return 'fall';
  if (m === 11 || m <= 1) return 'winter';
  if (m >= 2 && m <= 4) return 'spring';
  return 'summer';
}

function drawShape(ctx, shape, size) {
  ctx.beginPath();
  switch (shape) {
    case 'leaf':
      ctx.moveTo(0, -size);
      ctx.quadraticCurveTo(size * 0.9, -size * 0.2, 0, size);
      ctx.quadraticCurveTo(-size * 0.9, -size * 0.2, 0, -size);
      ctx.fill();
      ctx.globalAlpha *= 0.5;
      ctx.lineWidth = Math.max(1, size * 0.12);
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.beginPath();
      ctx.moveTo(0, -size * 0.8);
      ctx.lineTo(0, size * 0.9);
      ctx.stroke();
      return;
    case 'flake':
      ctx.lineWidth = Math.max(1.2, size * 0.18);
      ctx.lineCap = 'round';
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        const x = Math.cos(a) * size;
        const y = Math.sin(a) * size;
        ctx.moveTo(0, 0);
        ctx.lineTo(x, y);
        ctx.moveTo(x * 0.55, y * 0.55);
        ctx.lineTo(x * 0.55 + Math.cos(a + 0.9) * size * 0.3, y * 0.55 + Math.sin(a + 0.9) * size * 0.3);
      }
      ctx.stroke();
      return;
    case 'petal':
      ctx.ellipse(0, 0, size * 0.55, size, 0, 0, Math.PI * 2);
      ctx.fill();
      return;
    case 'cube': {
      const s = size * 1.5;
      const r = s * 0.22;
      ctx.roundRect(-s / 2, -s / 2, s, s, r);
      ctx.globalAlpha *= 0.75;
      ctx.fill();
      ctx.globalAlpha = Math.min(1, ctx.globalAlpha * 1.6);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.beginPath();
      ctx.roundRect(-s / 2 + s * 0.16, -s / 2 + s * 0.14, s * 0.32, s * 0.16, s * 0.08);
      ctx.fill();
      return;
    }
    default:
      ctx.arc(0, 0, size * 0.5, 0, Math.PI * 2);
      ctx.fill();
  }
}

class SeasonalEffects extends HTMLElement {
  #drift;
  #burstLayer;
  #toggle;
  #particles = [];
  #burst = [];
  #raf = 0;
  #burstRaf = 0;
  #lastScroll = 0;
  #scrollVelocity = 0;
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');
  #abort = new AbortController();
  #palette;
  #paused = false;

  connectedCallback() {
    const season = this.dataset.season === 'auto' ? seasonForDate() : this.dataset.season;
    this.#palette = PALETTES[season] ?? null;
    this.#drift = this.querySelector('canvas[data-drift]');
    this.#burstLayer = this.querySelector('canvas[data-burst-layer]');
    this.#toggle = this.querySelector('button[data-toggle]');
    try {
      this.#paused = localStorage.getItem(STORAGE_KEY) === '1';
    } catch {}

    const { signal } = this.#abort;
    addEventListener('resize', () => this.#resize(), { signal, passive: true });
    addEventListener('scroll', () => this.#onScroll(), { signal, passive: true });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });
    this.#reduced.addEventListener('change', () => this.#sync(), { signal });
    this.#toggle?.addEventListener('click', () => this.#setPaused(!this.#paused), { signal });
    if (this.hasAttribute('data-burst')) {
      document.addEventListener('s-cart:opened', () => this.burst(), { signal });
    }

    this.#lastScroll = scrollY;
    this.#resize();
    if (this.#palette && this.#drift) {
      this.#toggle?.removeAttribute('hidden');
      this.#seed();
    }
    this.#setPaused(this.#paused, false);
  }

  disconnectedCallback() {
    this.#abort.abort();
    cancelAnimationFrame(this.#raf);
    cancelAnimationFrame(this.#burstRaf);
  }

  get #motionOk() {
    return !this.#reduced.matches && !this.#paused;
  }

  #setPaused(paused, persist = true) {
    this.#paused = paused;
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, paused ? '1' : '0');
      } catch {}
    }
    if (this.#toggle) {
      this.#toggle.setAttribute('aria-pressed', String(paused));
      this.#toggle.setAttribute('aria-label', paused ? this.#toggle.dataset.labelPlay : this.#toggle.dataset.labelPause);
    }
    this.#sync();
  }

  #sync() {
    const run = this.#palette && this.#motionOk && !document.hidden;
    if (this.#drift) this.#drift.hidden = !(this.#palette && this.#motionOk);
    cancelAnimationFrame(this.#raf);
    if (run) this.#raf = requestAnimationFrame((t) => this.#tick(t));
  }

  #resize() {
    const dpr = Math.min(devicePixelRatio || 1, 2);
    for (const canvas of [this.#drift, this.#burstLayer]) {
      if (!canvas) continue;
      canvas.width = innerWidth * dpr;
      canvas.height = innerHeight * dpr;
      canvas.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }

  #onScroll() {
    const y = scrollY;
    this.#scrollVelocity = Math.max(-40, Math.min(40, this.#scrollVelocity + (y - this.#lastScroll) * 0.35));
    this.#lastScroll = y;
  }

  #seed() {
    const base = COUNTS[this.dataset.density] ?? COUNTS.medium;
    const count = innerWidth < 640 ? Math.ceil(base * 0.55) : base;
    this.#particles = Array.from({ length: count }, () => this.#makeParticle(true));
  }

  #makeParticle(anywhere = false) {
    const { colors } = this.#palette;
    return {
      x: Math.random() * innerWidth,
      y: anywhere ? Math.random() * innerHeight : -20,
      size: 5 + Math.random() * 7,
      vy: 0.35 + Math.random() * 0.7,
      sway: 0.6 + Math.random() * 1.4,
      phase: Math.random() * Math.PI * 2,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.03,
      alpha: 0.55 + Math.random() * 0.35,
      color: colors[Math.floor(Math.random() * colors.length)],
    };
  }

  #tick(time) {
    const ctx = this.#drift.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, innerWidth, innerHeight);
    const push = this.#scrollVelocity;
    this.#scrollVelocity *= 0.92;
    const shape = this.#palette.shape;
    for (const p of this.#particles) {
      p.y += p.vy - push * 0.12 * (p.size / 9);
      p.x += Math.sin(time / 1400 + p.phase) * p.sway * 0.35;
      p.rot += p.vr + push * 0.002;
      if (p.y > innerHeight + 24) Object.assign(p, this.#makeParticle(), { y: -20 });
      if (p.y < -30) Object.assign(p, this.#makeParticle(), { y: innerHeight + 20 });
      if (p.x < -20) p.x = innerWidth + 20;
      if (p.x > innerWidth + 20) p.x = -20;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = p.color;
      ctx.strokeStyle = p.color;
      drawShape(ctx, shape, p.size);
      ctx.restore();
    }
    this.#raf = requestAnimationFrame((t) => this.#tick(t));
  }

  /** Confetti burst from the top-centre of the viewport; paints above the cart drawer. */
  burst() {
    const layer = this.#burstLayer;
    if (!layer || this.#reduced.matches || this.#paused) return;
    const palette = this.#palette ?? PALETTES[seasonForDate()];
    const accent = getComputedStyle(document.documentElement).getPropertyValue('--color-accent').trim();
    const colors = accent ? [...palette.colors, accent] : palette.colors;
    const shapes = [palette.shape, 'petal', 'cube'];
    const originX = innerWidth * (document.dir === 'rtl' ? 0.3 : 0.7);
    const originY = innerHeight * 0.35;
    for (let i = 0; i < 48; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
      const speed = 6 + Math.random() * 8;
      this.#burst.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 4 + Math.random() * 5,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        life: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: shapes[Math.floor(Math.random() * shapes.length)],
      });
    }
    if (!layer.matches(':popover-open')) {
      try {
        layer.showPopover();
      } catch {}
    }
    cancelAnimationFrame(this.#burstRaf);
    this.#burstRaf = requestAnimationFrame(() => this.#burstTick());
  }

  #burstTick() {
    const layer = this.#burstLayer;
    const ctx = layer.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, innerWidth, innerHeight);
    this.#burst = this.#burst.filter((p) => p.life > 0);
    for (const p of this.#burst) {
      p.vy += 0.32;
      p.vx *= 0.985;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      p.life -= 0.012;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life * 1.4));
      ctx.fillStyle = p.color;
      ctx.strokeStyle = p.color;
      drawShape(ctx, p.shape, p.size);
      ctx.restore();
    }
    if (this.#burst.length) {
      this.#burstRaf = requestAnimationFrame(() => this.#burstTick());
    } else {
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      try {
        layer.hidePopover();
      } catch {}
    }
  }
}

if (!customElements.get('s-seasonal-effects')) customElements.define('s-seasonal-effects', SeasonalEffects);
